import { axiosInstance } from '@/utils/lib/axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { env } from '@/utils/constants/env';
import {
  INotification,
  NotificationListResponse,
  UnreadCountResponse,
  MarkReadResponse,
  Notifications,
} from '@/types/notification';

// Query Keys
export const notificationKeys = {
  all: ['notifications'] as const,
  list: (tenantId: number, page: number, pageSize: number) =>
    [...notificationKeys.all, 'list', tenantId, page, pageSize] as const,
  unreadCount: (tenantId: number) => [...notificationKeys.all, 'unread-count', tenantId] as const,
};

interface GetNotificationsParams {
  tenantId: number;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}

interface NotificationListData {
  notifications: INotification[];
  total: number;
  unread_count: number;
  page: number;
  page_size: number;
}

export const NotificationService = {
  // Get notifications list
  useGetNotifications: ({ tenantId, page = 1, pageSize = 20, enabled = true }: GetNotificationsParams) =>
    useQuery<NotificationListData>({
      queryKey: notificationKeys.list(tenantId, page, pageSize),
      queryFn: async () => {
        const { data } = await axiosInstance.get<NotificationListResponse>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/notifications/${tenantId}`,
          {
            params: { page, page_size: pageSize },
          },
        );
        return data.data;
      },
      enabled: enabled && tenantId > 0,
    }),

  // Get unread count
  useGetUnreadCount: (tenantId: number, enabled = true) =>
    useQuery<number>({
      queryKey: notificationKeys.unreadCount(tenantId),
      queryFn: async () => {
        const { data } = await axiosInstance.get<UnreadCountResponse>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/notifications/${tenantId}/unread-count`,
        );
        return data.data.unread_count;
      },
      enabled: enabled && tenantId > 0,
      refetchInterval: 30000, // Refetch every 30 seconds
    }),

  // Mark single notification as read
  useMarkAsRead: () => {
    const queryClient = useQueryClient();

    return useMutation<MarkReadResponse, Error, { tenantId: number; notificationId: string }>({
      mutationFn: async ({ tenantId, notificationId }) => {
        const { data } = await axiosInstance.post<MarkReadResponse>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/notifications/${tenantId}/${notificationId}/read`,
        );
        return data;
      },
      onSuccess: (_, { tenantId }) => {
        // Invalidate related queries
        queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      },
    });
  },

  // Mark all notifications as read
  useMarkAllAsRead: () => {
    const queryClient = useQueryClient();

    return useMutation<MarkReadResponse, Error, { tenantId: number }>({
      mutationFn: async ({ tenantId }) => {
        const { data } = await axiosInstance.post<MarkReadResponse>(
          `${env.apiTasklistBaseUrl}/api/v1/compro/notifications/${tenantId}/mark-all-read`,
        );
        return data;
      },
      onSuccess: (_, { tenantId }) => {
        // Invalidate related queries
        queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      },
    });
  },

  // Fetch notifications (non-hook version for Zustand)
  fetchNotifications: async (tenantId: number, page = 1, pageSize = 20): Promise<NotificationListData> => {
    const { data } = await axiosInstance.get<NotificationListResponse>(
      `${env.apiTasklistBaseUrl}/api/v1/compro/notifications/${tenantId}`,
      {
        params: { page, page_size: pageSize },
      },
    );
    return data.data;
  },

  // Fetch unread count (non-hook version for Zustand)
  fetchUnreadCount: async (tenantId: number): Promise<number> => {
    const { data } = await axiosInstance.get<UnreadCountResponse>(
      `${env.apiTasklistBaseUrl}/api/v1/compro/notifications/${tenantId}/unread-count`,
    );
    return data.data.unread_count;
  },

  // Mark as read (non-hook version)
  markAsRead: async (tenantId: number, notificationId: string): Promise<void> => {
    await axiosInstance.post(
      `${env.apiTasklistBaseUrl}/api/v1/compro/notifications/${tenantId}/${notificationId}/read`,
    );
  },

  // Mark all as read (non-hook version)
  markAllAsRead: async (tenantId: number): Promise<void> => {
    await axiosInstance.post(`${env.apiTasklistBaseUrl}/api/v1/compro/notifications/${tenantId}/mark-all-read`);
  },

  // Legacy API support
  useGetNotification: ({
    page = 0,
    limit = 100,
    isRead,
    userId,
  }: {
    page: number;
    limit: number;
    isRead: boolean | null;
    userId: string;
  }) =>
    useQuery<Notifications>({
      queryKey: ['legacy-notifications', page, limit, isRead, userId],
      queryFn: async () => {
        const url = new URL(`${env.apiTasklistBaseUrl}/api/v1/notifications`);

        // Add parameters
        url.searchParams.append('page', page.toString());
        url.searchParams.append('limit', limit.toString());

        // Only add isRead if it has a value
        if (isRead !== undefined && isRead !== null) {
          url.searchParams.append('isRead', isRead.toString());
        }

        url.searchParams.append('userId', userId);

        const { data } = await axiosInstance.get(url.toString());

        return data.data;
      },
    }),

  fetchLegacyNotifications: async ({
    page = 0,
    limit = 100,
    isRead,
    userId,
  }: {
    page: number;
    limit: number;
    isRead: boolean | null;
    userId: string;
  }) => {
    const response = await axiosInstance.get(`${env.apiTasklistBaseUrl}/api/v1/notifications`, {
      params: {
        page,
        limit,
        isRead,
        userId,
      },
    });
    return response.data.data;
  },
};
